/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { modal } from "commonAssets/lib/modal.js";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError.js";

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log("[Initiate claim]...");

    const disableIfHasValue = (id) => {
        const field = document.getElementById(id);
        if (!field) return;
        field.toggleAttribute("disabled", Boolean(field.value));
    };

    disableIfHasValue("first_name");
    disableIfHasValue("last_name");

    const toggleHidden = (element, hidden) => {
        if (!element) return;
        element.classList.toggle("d-none", Boolean(hidden));
    };

    const setDisabled = (elements, disabled) => {
        elements
            .filter(Boolean)
            .forEach((element) => element.toggleAttribute("disabled", disabled));
    };

    const setRequired = (elements, required) => {
        elements
            .filter(Boolean)
            .forEach((element) => element.toggleAttribute("required", required));
    };

    const clearValues = (elements) => {
        elements.filter(Boolean).forEach((element) => {
            if ("value" in element) element.value = "";
        });
    };

    const todayLocalISODate = () => {
        const now = new Date();
        const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 10);
    };

    const showExclusiveSelectionModal = (keepCheckedLabel, tryCheckedLabel) => {
        modal(
            "Selection Required",
            `<p>You can only select one option.</p><p>Please unselect <strong>${keepCheckedLabel}</strong> to select <strong>${tryCheckedLabel}</strong>.</p>`
        );
    };

    const moveOutOfPocketTable = () => {
        const wrapper = document.querySelector('[data-field-id="__wrapper_9"]');
        const table = document.querySelector("#table_responsive_1");
        const addTransactionsButton = document.querySelector(
            "#add_transactions_1"
        );
        const addTransactionsParent = addTransactionsButton?.parentElement;
        const transactionsPlaceholder = document.querySelector("#transactions");
        const claimFieldsTitle = document.querySelector("#claimFieldsTitle");
        const claimFieldsBody = document.querySelector("#claimFieldsBody");

        // Amount Requested field wrapper - to be relocated below the table
        const dlrAmountWrapper = document.querySelector(
            '[data-field-id="_documented_loss_amount_requested"]'
        );

        if (!wrapper || !table) return false;

        if (table.previousElementSibling !== wrapper) {
            wrapper.insertAdjacentElement("afterend", table);
        }

        if (addTransactionsParent) {
            if (!addTransactionsParent.id) addTransactionsParent.id = "add_remove_btns";
            addTransactionsParent.classList.add("mb-4");
        }

        if (
            addTransactionsParent &&
            table.nextElementSibling !== addTransactionsParent
        ) {
            table.insertAdjacentElement("afterend", addTransactionsParent);
        }

        // Move Amount Requested field below the add/delete buttons
        if (dlrAmountWrapper && addTransactionsParent) {
            addTransactionsParent.insertAdjacentElement("afterend", dlrAmountWrapper);
        }

        transactionsPlaceholder?.remove();
        claimFieldsTitle?.remove();
        claimFieldsBody?.remove();

        return true;
    };

    const initClaimFieldBehavior = () => {
        const alt = document.querySelector(
            'input[type="checkbox"][name="alternative_cash_payment"]'
        );
        const dlr = document.querySelector(
            'input[type="checkbox"][name="documented_loss_reimbursement"]'
        );
        const ca = document.querySelector(
            'input[type="checkbox"][name="california_statutory_cash_payment"]'
        );

        const dlrAmount = document.querySelector(
            "#documented_loss_amount_requested"
        );
        const table = document.querySelector("#table_responsive_1");
        const addTransactionsParent =
            document.querySelector("#add_transactions_1")?.parentElement ?? null;

        const dlrWrapper6 = document.querySelector(
            '[data-field-id="__wrapper_6"]'
        );
        const dlrWrapper8 = document.querySelector(
            '[data-field-id="__wrapper_8"]'
        );
        const dlrWrapper9 = document.querySelector(
            '[data-field-id="__wrapper_9"]'
        );
        const dlrWrapper10 = document.querySelector(
            '[data-field-id="__wrapper_10"]'
        );
        const dlrAmountWrapper = document.querySelector(
            '[data-field-id="_documented_loss_amount_requested"]'
        );

        const caWrapper14 = document.querySelector(
            '[data-field-id="__wrapper_14"]'
        );
        const caAttestationWrapper = document.querySelector(
            '[data-field-id="_california_residency_attestation"]'
        );
        const caDateWrapper = document.querySelector(
            '[data-field-id="_california_residency_date"]'
        );
        const caAttestation = document.querySelector(
            "#california_residency_attestation"
        );
        const caDate = document.querySelector("#california_residency_date");

        // Documentation section elements
        const documentationSection = document.querySelector("#documentation");

        if (!alt || !dlr) return false;

        // Calculate and update total amount from transaction rows
        const updateAmountRequestedTotal = () => {
            if (!dlrAmount || !table) return;
            // Use starts-with selector to match trx_amount, trx_amount[], etc.
            const amountInputs = table.querySelectorAll(
                'input[name^="trx_amount"]'
            );
            let total = 0;
            amountInputs.forEach((input) => {
                const val = parseFloat(input.value);
                if (!isNaN(val)) total += val;
            });
            // Cap at max (5000) and round to 2 decimal places
            const maxAmount = parseFloat(dlrAmount.max) || 5000;
            total = Math.min(total, maxAmount);
            dlrAmount.value = total > 0 ? total.toFixed(2) : "";
        };

        // Attach listeners to transaction amount inputs (including dynamically added rows)
        const attachAmountListeners = () => {
            if (!table) return;
            table.addEventListener("input", (e) => {
                // Match trx_amount, trx_amount[], etc.
                if (e.target.name?.startsWith("trx_amount")) {
                    updateAmountRequestedTotal();
                }
            });

            // Also recalculate when rows are deleted
            const removeRowBtn = document.querySelector(
                "#remove_transactions_1"
            );
            if (removeRowBtn) {
                removeRowBtn.addEventListener("click", () => {
                    // Short delay to allow DOM to update after row removal
                    setTimeout(updateAmountRequestedTotal, 50);
                });
            }
        };

        // Update documentation section visibility based on DLR or CA selection
        const updateDocumentationVisibility = () => {
            const dlrChecked = dlr?.checked ?? false;
            const caChecked = ca?.checked ?? false;
            const showDocs = dlrChecked || caChecked;
            toggleHidden(documentationSection, !showDocs);
        };

        const applyDlrState = (enabled) => {
            toggleHidden(dlrAmountWrapper, !enabled);
            toggleHidden(table, !enabled);
            toggleHidden(addTransactionsParent, !enabled);
            toggleHidden(dlrWrapper6, !enabled);
            toggleHidden(dlrWrapper10, !enabled);

            // These two paragraphs are duplicated by the transaction table caption/headers.
            // Keep the wrapper nodes in the DOM (used as stable anchors) but always hide them.
            toggleHidden(dlrWrapper8, true);
            toggleHidden(dlrWrapper9, true);

            const tableInputs = table
                ? Array.from(table.querySelectorAll("input, select, textarea"))
                : [];

            setDisabled([dlrAmount, ...tableInputs], !enabled);
            setRequired([dlrAmount, ...tableInputs], enabled);

            // Make Amount Requested readonly (auto-calculated from transaction rows)
            if (dlrAmount) dlrAmount.toggleAttribute("readonly", enabled);

            if (!enabled) {
                clearValues([dlrAmount, ...tableInputs]);

                const tbody = table?.querySelector("#transactions_1 > tbody");
                if (tbody) {
                    const rows = Array.from(tbody.querySelectorAll("tr"));
                    rows.slice(1).forEach((row) => row.remove());
                }

                const removeRowBtn =
                    document.querySelector("#remove_transactions_1");
                removeRowBtn?.classList.add("disabled");
                removeRowBtn?.setAttribute("aria-disabled", "true");
            }

            // Update documentation visibility when DLR state changes
            updateDocumentationVisibility();
        };

        const applyCaState = (enabled) => {
            toggleHidden(caWrapper14, !enabled);
            toggleHidden(caAttestationWrapper, !enabled);
            toggleHidden(caDateWrapper, !enabled);

            setDisabled([caAttestation, caDate], !enabled);
            setRequired([caAttestation], enabled);

            if (caDate) {
                caDate.toggleAttribute("readonly", enabled);
                if (enabled) caDate.value = todayLocalISODate();
            }

            if (!enabled) {
                clearValues([caAttestation, caDate]);
                caDate?.removeAttribute("readonly");
            }

            // Update documentation visibility when CA state changes
            updateDocumentationVisibility();
        };

        const enforceExclusiveSelection = (target, other) => {
            if (!target.checked || !other.checked) return true;

            target.checked = false;
            showExclusiveSelectionModal(
                other.dataset.label || other.name,
                target.dataset.label || target.name
            );
            return false;
        };

        alt.addEventListener("change", () => {
            enforceExclusiveSelection(alt, dlr);
        });

        dlr.addEventListener("change", () => {
            if (!enforceExclusiveSelection(dlr, alt)) return;
            applyDlrState(dlr.checked);
        });

        applyDlrState(dlr.checked);

        if (ca) {
            ca.addEventListener("change", () => applyCaState(ca.checked));
            applyCaState(ca.checked);
        }

        // Initialize auto-sum listener for transaction amounts
        attachAmountListeners();

        // Set initial documentation visibility
        updateDocumentationVisibility();

        return true;
    };

    const tryInit = () => {
        const moved = moveOutOfPocketTable();
        const inited = initClaimFieldBehavior();
        return moved && inited;
    };

    if (!tryInit()) {
        const observer = new MutationObserver(() => {
            if (tryInit()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 15000);
    }

    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");
    // custom code goes here
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...");

    const dlr = document.querySelector(
        'input[type="checkbox"][name="documented_loss_reimbursement"]'
    );
    const dlrChecked = Boolean(dlr?.checked);
    const acp = document.querySelector(
        'input[type="checkbox"][name="alternative_cash_payment"]'
    );
    const acpChecked = Boolean(acp?.checked);

    // ACP or DLR selection is required (mutually exclusive).
    if (!acpChecked && !dlrChecked) {
        claim.flags.valid = false;
        if (acp) toggleFieldError(acp, true, "Required");
        if (dlr) toggleFieldError(dlr, true, "Required");
        modal(
            "Selection Required",
            "<p>Please select either <strong>Alternative Cash Payment</strong> or <strong>Documented Loss Reimbursement</strong>.</p>"
        );
        return claim;
    }

    if (acpChecked && dlrChecked) {
        claim.flags.valid = false;
        if (acp) toggleFieldError(acp, true, "Select only one");
        if (dlr) toggleFieldError(dlr, true, "Select only one");
        modal(
            "Selection Required",
            "<p>Please select only one option: <strong>Alternative Cash Payment</strong> or <strong>Documented Loss Reimbursement</strong>.</p>"
        );
        return claim;
    }

    if (acp) toggleFieldError(acp, false);
    if (dlr) toggleFieldError(dlr, false);

    const fileInput = document.getElementById("file-upload");
    const documentationSection = document.getElementById("documentation");

    const newUploadsCount = Array.isArray(claim?.uploads)
        ? claim.uploads.length
        : 0;
    const existingFiles = Array.isArray(claim?.class_member?.claim_files)
        ? claim.class_member.claim_files
        : [];
    const existingSuccessfulCount = existingFiles.filter(
        (f) => f && f.failed !== true
    ).length;

    if (dlrChecked) {
        const hasAnyDocumentation =
            newUploadsCount > 0 || existingSuccessfulCount > 0;

        if (!hasAnyDocumentation) {
            claim.flags.valid = false;

            if (fileInput && documentationSection) {
                toggleFieldError(fileInput, true, "Required");
                documentationSection.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                fileInput.focus();
            }

            return claim;
        }

        if (fileInput) toggleFieldError(fileInput, false);
        return claim;
    }

    // If DLR is not selected, remove any documentation errors.
    if (fileInput) toggleFieldError(fileInput, false);
    return claim;
}
